import { motion } from "framer-motion";
import { Layers, Wallpaper, Printer, Shirt, Store, ArrowUpRight } from "lucide-react";
import { SERVICES } from "../data/siteData";
import { Reveal, EASE } from "./Reveal";

const ICONS = {
    personalizzazioni: Layers,
    "carte-da-parati": Wallpaper,
    tipografia: Printer,
    gadget: Shirt,
    allestimenti: Store,
};

const ACCENTS = ["#0099FF", "#FF0066", "#FFCC00", "#0E0E10", "#D4A373"];

export const Services = () => {
    return (
        <section id="servizi" data-testid="services-section" className="py-16 sm:py-24 lg:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal className="max-w-2xl">
                    <p className="font-mono-label text-xs text-[#FF0066] mb-4">Servizi</p>
                    <h2 data-testid="services-title" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-[#0E0E10]">
                        Cinque aree, un unico laboratorio
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-zinc-600 leading-relaxed">
                        Seguiamo ogni progetto dalla prima bozza alla consegna finale: progettiamo,
                        produciamo e posiamo direttamente, senza intermediari.
                    </p>
                </Reveal>

                <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {SERVICES.map((service, i) => {
                        const Icon = ICONS[service.id];
                        return (
                            <motion.article
                                key={service.id}
                                data-testid={`service-card-${service.id}`}
                                className="group relative h-full bg-white border border-zinc-200 p-6 sm:p-8 hover:border-[#0E0E10] hover:shadow-[0_20px_40px_rgba(0,0,0,0.10)] transition-[border-color,box-shadow] duration-300 flex flex-col"
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: EASE }}
                                whileHover={{ y: -6 }}
                            >
                                <span className="absolute top-0 left-0 h-1 w-12 transition-all duration-300 group-hover:w-full" style={{ background: ACCENTS[i % ACCENTS.length] }} />
                                <div className="flex items-start justify-between">
                                    <span className="p-3 border border-zinc-200 text-[#0E0E10] group-hover:border-[#FF0066] group-hover:text-[#FF0066] transition-colors duration-300">
                                        <Icon size={24} />
                                    </span>
                                    <span className="font-mono-label text-xs text-zinc-400">0{i + 1}</span>
                                </div>
                                <h3 className="mt-6 font-heading text-xl font-bold text-[#0E0E10]">{service.title}</h3>
                                <p className="mt-1 text-sm font-medium text-[#FF0066]">{service.subtitle}</p>
                                <p className="mt-3 text-sm leading-relaxed text-zinc-600 flex-1">{service.description}</p>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {service.tags.map((tag) => (
                                        <span key={tag} className="text-xs bg-[#FAF8F5] border border-zinc-200 text-zinc-600 px-2.5 py-1">{tag}</span>
                                    ))}
                                </div>
                                <a
                                    href="#contatti"
                                    data-testid={`service-cta-${service.id}`}
                                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E0E10] group-hover:text-[#FF0066] transition-colors duration-300"
                                >
                                    Richiedi questo servizio
                                    <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </a>
                            </motion.article>
                        );
                    })}
                    <Reveal delay={0.2} className="h-full">
                        <div className="relative h-full bg-[#0E0E10] p-6 sm:p-8 flex flex-col justify-between" data-testid="services-cta-card">
                            <div className="cmyk-strip absolute top-0 left-0 right-0">
                                <span className="c" /><span className="m" /><span className="y" /><span className="k" />
                            </div>
                            <h3 className="mt-4 font-heading text-xl sm:text-2xl font-bold text-white leading-snug">
                                Hai un'idea che non trovi in lista?
                            </h3>
                            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                                Raccontacela: la progettazione su misura è il cuore del nostro lavoro.
                            </p>
                            <a
                                href="#contatti"
                                data-testid="services-custom-cta"
                                className="mt-6 inline-flex items-center justify-center gap-2 bg-[#FF0066] text-white text-sm font-semibold px-6 py-3.5 hover:bg-white hover:text-[#0E0E10] transition-colors duration-300"
                            >
                                Parliamone insieme <ArrowUpRight size={15} />
                            </a>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
